import React from "react"
import PropTypes from "prop-types"
import moment from "moment-timezone"

import Logger from "@common/Logger"
import { GRAPH_COLORS } from "@common/module/EcosuiteView"
import ProjectUtils from "@common/utils/ProjectUtils"
import Aggregations from "@common/Aggregations"
import EcosuiteComponent, { Loading } from "@common/EcosuiteComponent"
import GraphUtils from "@common/utils/GraphUtils"

import EnergyService from "@dashboard/energy/EnergyService"
import WeatherService from "@dashboard/energy/WeatherService"
import DateRangeGraph from "../DateRangeGraph"
import { Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import EnergyUtils from "@dashboard/energy/EnergyUtils"
import DeviceUtils from "@common/utils/DeviceUtils"

export default class SourceGenerationLineGraph extends EcosuiteComponent {
  componentDidMount() {
    super.componentDidMount()
    this.getDaylight()
    this.loadDatums()
  }

  componentDidUpdate(prevProps) {
    super.componentDidUpdate(prevProps)
    if (
      this.props.project !== prevProps.project ||
      this.props.site !== prevProps.site ||
      this.props.range !== prevProps.range
    ) {
      this.getDaylight()
      this.loadDatums()
    }
  }

  loadDatums() {
    const range = this.props.range
    const aggregation = this.props.aggregation
    const projectId = this.props.project.code
    const sourceIdPattern = ProjectUtils.getPath(this.props.project, this.props.site) + "/**/INV/**"

    EnergyService.getNodesDatums(range, aggregation, projectId, undefined, undefined, sourceIdPattern)
      .then((response) => {
        if (this.isProjectCurrent(projectId) && this.isAggregationCurrent(aggregation) && this.isRangeCurrent(range)) {
          // Store raw datums in state so they can be reprocessed when daylight data arrives
          this.setStateIfMounted(
            {
              rawDatums: response.data,
              range: response.range,
              aggregation: response.aggregation,
            },
            () => {
              // Process datums immediately if we already have daylight data
              if (this.state?.daylightMinutes) {
                this.processDatums(response.data, response.range, response.aggregation)
              }
            },
          )
        } else {
          Logger.debug(`Ignoring out of date repsonse`)
        }
      })
      .catch((error) => {
        Logger.error("SourceGenerationLineGraph: Error loading datums", error)
      })
  }

  processDatums(datums, range, aggregation) {
    const devices = {}
    Object.values(this.props.site.systems).forEach((system) => {
      system.devices.forEach((device) => {
        if (DeviceUtils.isInverter(device)) {
          const devicePath = ProjectUtils.getPath(this.props.project, this.props.site, system, device)
          devices[devicePath] = device
        }
      })
    })

    const sources = this.getSources(devices, datums, range, aggregation)
    this.setStateIfMounted({ sources: sources })
  }

  getDaylight() {
    const range = this.props.range
    const projectId = this.props.project.code

    WeatherService.getProjectDaylight(projectId, range)
      .then((response) => {
        if (this.isProjectCurrent(projectId) && this.isRangeCurrent(range)) {
          const daylightData = response.daylight[this.props.project.code].sites[this.props.site.code]

          this.setStateIfMounted(
            {
              daylightMinutes: daylightData,
            },
            () => {
              // If we have raw datums waiting, reprocess them now that we have daylight data
              if (this.state?.rawDatums) {
                this.processDatums(this.state.rawDatums, this.state.range, this.state.aggregation)
              }
            },
          )
        } else {
          Logger.debug(`Ignoring out of date response`)
        }
      })
      .catch((error) => {
        Logger.error("SourceGenerationLineGraph: Error loading daylight", error)
      })
  }

  getSources(devices, datums, range, aggregation) {
    const sourceIds = Object.keys(devices)
    const filteredDatums = datums.filter((datum) => sourceIds.includes(datum.sourceId))

    let sources = {}
    let date = moment(range.localStartDate)
    let end = moment(range.localEndDate)
    while (date.isBefore(end)) {
      let dateKey = date.format("YYYY-MM-DDTHH:mm")
      sources[dateKey] = {
        time: moment(dateKey).valueOf(),
      }
      date.add(Aggregations.getSize(aggregation), Aggregations.getUnits(aggregation))
    }

    // Process filtered datums
    filteredDatums.forEach((datum) => {
      const dateKey = `${datum.localDate}T${datum.localTime}`

      if (!this.state?.daylightMinutes) {
        return
      }

      const daylightHours = this.state.daylightMinutes[dateKey] / 60

      if (datum.wattHours) {
        if (sources[dateKey]) {
          const device = devices[datum.sourceId]
          if (device?.dcSize) {
            sources[dateKey][datum.sourceId] = EnergyUtils.getNormalisedGeneration(
              device.dcSize,
              datum.wattHours,
              aggregation,
              daylightHours,
            )
          }
        } else {
          Logger.warning(`Ignoring unexpected local date in results: ${dateKey} for source: ${datum.sourceId}`)
        }
      }
    })

    return {
      sourceIds: Array.from(sourceIds),
      data: Object.values(sources),
    }
  }

  renderContent() {
    if (this.state?.daylightMinutes && this.state?.sources) {
      return (
        <div className="source-generation-graph">
          <SourceGraph
            {...this.props}
            range={this.state.range}
            aggregation={this.state.aggregation}
            sources={this.state.sources}
          />
        </div>
      )
    } else {
      return (
        <div className="source-generation-graph">
          <Loading />
        </div>
      )
    }
  }
}

class SourceGraph extends DateRangeGraph {
  getDisplayName(sourceId) {
    // Extract just the inverter number from the full path
    // e.g., "/MA1/ANNE/R1/INV/9" becomes "INV 9"
    const parts = sourceId.split("/")
    const invIndex = parts.findIndex((part) => part === "INV")
    if (invIndex >= 0 && invIndex < parts.length - 1) {
      return `INV ${parts[invIndex + 1]}`
    }
    return sourceId
  }

  renderContent() {
    let sources = this.props.sources

    if (!sources || !sources.data || sources.data.length === 0) {
      return (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>No data available for normalized inverter graph</p>
        </div>
      )
    }

    if (!sources.sourceIds || sources.sourceIds.length === 0) {
      return (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>No inverter sources found</p>
        </div>
      )
    }

    return (
      <ResponsiveContainer width="100%" height={250}>
        <LineChart
          data={sources.data}
          margin={{ top: 5, right: 30, left: 20, bottom: 10 }}
          onClick={this.zoomDateRange}
        >
          <XAxis
            dataKey="time"
            type="number"
            scale="time"
            domain={this.getDomain(this.props.range, this.props.aggregation)}
            tickFormatter={(time) => {
              return GraphUtils.dateFormat(time, this.props.range, this.props.aggregation)
            }}
          />
          <YAxis />
          <Tooltip
            content={
              <SourceTooltip unit={this.props.unit} range={this.props.range} aggregation={this.props.aggregation} />
            }
          />
          <Legend iconType="plainline" />
          {sources.sourceIds.map((sourceId, idx) => {
            const color = GRAPH_COLORS[idx % GRAPH_COLORS.length]
            const displayName = this.getDisplayName(sourceId)
            const graphType =
              this.props.aggregation === "day" ||
              this.props.aggregation === "month" ||
              this.props.aggregation === "year"
                ? "linear"
                : "monotone"
            return (
              <Line
                key={sourceId}
                name={displayName}
                type={graphType}
                dataKey={sourceId}
                stroke={color}
                fill={color}
                dot={false}
                strokeWidth={2}
                isAnimationActive={false}
              />
            )
          })}
          {this.renderCurrentTime()}
        </LineChart>
      </ResponsiveContainer>
    )
  }
}

class SourceTooltip extends EcosuiteComponent {
  static propTypes = {
    type: PropTypes.string,
    payload: PropTypes.array,
    label: PropTypes.number,
  }

  renderContent() {
    const { active } = this.props
    if (active) {
      const { payload, label } = this.props
      if (payload) {
        return (
          <div className="area-graph-tooltip">
            <p className="label">{GraphUtils.formatDateTooltip(label, this.props.range, this.props.aggregation)}</p>
            {payload.map((data) => {
              return (
                <React.Fragment key={data.dataKey}>
                  <p className="label" style={{ color: data.fill }}>
                    {`${data.name} : ${data.value}`}
                  </p>
                </React.Fragment>
              )
            })}
          </div>
        )
      }
    }
    return null
  }
}
